import { useMemo, useState, type DragEvent, type ReactNode } from 'react'
import { AGG_LABELS, ALL_VIEWS, BUCKET_LABELS, PERIOD_LABELS, VIEW_LABELS } from './describe'
import { plannedColumns, pruneDisplay, remapColumns } from './drill'
import { fittingView, SET_GROUPS, setGroup } from './groups'
import { fold } from './text'
import type { Bucket, CatalogField, CatalogSet, Definition, Display, Filter, FilterOp, Locale, Measure, PeriodKey, Rule, RuleColor, ValueItem, View } from './types'

/**
 * CONSTRUCTORUL PAS CU PAS: aceeasi definitie ca bara, aratata in cinci pasi
 * numerotati - 1 ce date, 2 ce masori, 3 grupat pe (optional), 4 filtre si
 * perioada, 5 cum arata. Fara fraza: `definition` poate fi null, si atunci
 * se vede doar pasul 1, alegerea datelor.
 *
 * Pe langa forma de baza:
 *  - pasul 2: pana la cinci masuri (prima e cea a graficului), coloanele
 *    listei alese, mutate si scoase;
 *  - pasul 3: doua niveluri de grupare, felia de timp, ordinea, cate randuri,
 *    restul ca "altele", randul de total si ponderea din total;
 *  - pasul 4: perioadele relative sau un interval, comparatia cu perioada
 *    dinainte, filtre legate cu "toate" sau "oricare" ("intre", "este unul
 *    din", "e gol"...), si filtrele salvate pe set (numai in browserul omului);
 *  - pasul 5: vederea, tinta cifrei, regulile de culoare si numele coloanelor.
 *
 * Tot ce e de afisare sta in `display` si nu schimba interogarea; serverul il
 * verifica la fel ca restul definitiei.
 */

const OPS: Record<CatalogField['type'], FilterOp[]> = {
  text: ['contains', 'eq', 'neq', 'ncontains', 'starts', 'in', 'nin', 'empty', 'nempty'],
  enum: ['in', 'nin', 'contains', 'empty', 'nempty'],
  bool: ['eq'],
  number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'empty', 'nempty'],
  duration: ['gt', 'gte', 'lt', 'lte', 'between', 'empty', 'nempty'],
  bytes: ['gt', 'gte', 'lt', 'lte', 'between', 'empty', 'nempty'],
  date: ['gte', 'lt', 'between', 'empty', 'nempty'],
}

const OP_LABELS: Record<FilterOp, { ro: string; en: string }> = {
  eq: { ro: 'este', en: 'is' },
  neq: { ro: 'nu este', en: 'is not' },
  in: { ro: 'este unul din', en: 'is one of' },
  nin: { ro: 'nu este', en: 'is not' },
  contains: { ro: 'contine', en: 'contains' },
  ncontains: { ro: 'nu contine', en: 'does not contain' },
  starts: { ro: 'incepe cu', en: 'starts with' },
  gt: { ro: 'mai mare de', en: 'greater than' },
  gte: { ro: 'cel putin', en: 'at least' },
  lt: { ro: 'mai mic de', en: 'less than' },
  lte: { ro: 'cel mult', en: 'at most' },
  between: { ro: 'intre', en: 'between' },
  empty: { ro: 'e gol', en: 'is empty' },
  nempty: { ro: 'e completat', en: 'is filled in' },
}

/** Pe un text, "este unul din" / "nu este unul din" iau o lista scrisa cu virgula. */
const TEXT_LIST_LABELS: Partial<Record<FilterOp, { ro: string; en: string }>> = {
  in: { ro: 'este unul din (lista)', en: 'is one of (list)' },
  nin: { ro: 'nu este unul din (lista)', en: 'is none of (list)' },
}

const PERIODS: PeriodKey[] = ['all', 'today', 'yesterday', 'this_week', 'last_week', 'this_month', 'last_month', 'this_quarter', 'last_quarter', 'this_year', 'last_year', 'last_7_days', 'last_30_days', 'last_90_days', 'last_n_days', 'custom', 'next_7_days', 'next_30_days', 'next_90_days', 'next_month']

/** Vederile, cu un semn mic de recunoscut. */
const MARKS: Record<View, string> = {
  table: '☰',
  kpi: '#',
  gauge: '◠',
  bar: '▤',
  stacked: '▥',
  line: '⟋',
  area: '◭',
  topn: '↧',
  pie: '◔',
  pivot: '▦',
}

/** Ce forma cere vederea, ca sa se spuna cand nu se potriveste. */
const NEEDS: Partial<Record<View, 'nogroup' | 'group' | 'two'>> = {
  kpi: 'nogroup',
  gauge: 'nogroup',
  bar: 'group',
  line: 'group',
  area: 'group',
  topn: 'group',
  pie: 'group',
  stacked: 'two',
  pivot: 'group',
}

const BUCKETS: Bucket[] = ['day', 'week', 'month', 'quarter', 'year']

/** Socotelile, spuse in cuvinte simple. `list` = fara socoteala, randurile ca atare. */
type Shape = 'list' | Measure['agg']
const SHAPES: { shape: Shape; mark: string; label: { ro: string; en: string } }[] = [
  { shape: 'list', mark: '☰', label: { ro: 'Arata randurile', en: 'Show the rows' } },
  { shape: 'count', mark: '#', label: { ro: 'Numara', en: 'Count' } },
  { shape: 'sum', mark: 'Σ', label: { ro: 'Aduna', en: 'Add up' } },
  { shape: 'avg', mark: 'x̄', label: { ro: 'Media', en: 'Average' } },
]
const MORE_AGGS: Measure['agg'][] = ['ratio', 'count_distinct', 'min', 'max']
const MORE_LABELS: Partial<Record<Measure['agg'], { ro: string; en: string }>> = {
  ratio: { ro: 'Procent', en: 'Percent' },
  count_distinct: { ro: 'Distincte', en: 'Distinct' },
  min: { ro: 'Minim', en: 'Minimum' },
  max: { ro: 'Maxim', en: 'Maximum' },
}
const ALL_AGGS: Measure['agg'][] = ['count', 'sum', 'avg', 'ratio', 'count_distinct', 'min', 'max']
const aggWord = (agg: Measure['agg']) => SHAPES.find((s) => s.shape === agg)?.label ?? MORE_LABELS[agg]!

/** "Altele" si ponderea au sens numai unde randurile se aduna. */
const ADDITIVE: Measure['agg'][] = ['count', 'sum']

const RULE_OPS: { op: Rule['op']; text: string }[] = [
  { op: 'gt', text: '>' },
  { op: 'gte', text: '≥' },
  { op: 'lt', text: '<' },
  { op: 'lte', text: '≤' },
  { op: 'eq', text: '=' },
]
const RULE_COLORS: { color: RuleColor; label: { ro: string; en: string } }[] = [
  { color: 'red', label: { ro: 'rosu', en: 'red' } },
  { color: 'amber', label: { ro: 'galben', en: 'amber' } },
  { color: 'green', label: { ro: 'verde', en: 'green' } },
  { color: 'blue', label: { ro: 'albastru', en: 'blue' } },
]

const MAX_MEASURES = 5

/* ---------- filtrele salvate: numai in browserul omului, pe set ---------- */

interface FilterPreset {
  name: string
  filters: Filter[]
  match?: 'all' | 'any'
}

const PRESETS_KEY = 'od-rep-filter-presets'

function readPresets(dataset: string): FilterPreset[] {
  try {
    const all = JSON.parse(window.localStorage.getItem(PRESETS_KEY) ?? '{}') as Record<string, FilterPreset[]>
    return Array.isArray(all[dataset]) ? all[dataset]! : []
  } catch {
    return []
  }
}

function writePresets(dataset: string, list: FilterPreset[]): void {
  try {
    const all = JSON.parse(window.localStorage.getItem(PRESETS_KEY) ?? '{}') as Record<string, FilterPreset[]>
    all[dataset] = list.slice(0, 20)
    window.localStorage.setItem(PRESETS_KEY, JSON.stringify(all))
  } catch {
    /* Fara loc in browser (fereastra privata): filtrele raman in raport, doar nu se tin minte. */
  }
}

interface Props {
  catalog: CatalogSet[]
  definition: Definition | null
  values: Record<string, ValueItem[]>
  locale: Locale
  onChange: (next: Definition) => void
}

function Step({ n, title, hint, disabled, children }: { n: number; title: string; hint?: string; disabled?: boolean; children: ReactNode }) {
  return (
    <section className={`od-rep-step${disabled ? ' od-rep-step-off' : ''}`} aria-disabled={disabled || undefined}>
      <div className="od-rep-step-head">
        <span className="od-rep-step-n">{n}</span>
        <h4>{title}</h4>
        {hint ? <span className="od-rep-step-hint">{hint}</span> : null}
      </div>
      {children ? <div className="od-rep-step-body">{children}</div> : null}
    </section>
  )
}

/** Pasul 1: seturile pe familii, cu cautare. */
function DataPicker({ catalog, current, locale, onPick }: { catalog: CatalogSet[]; current: string | null; locale: Locale; onPick: (key: string) => void }) {
  const [query, setQuery] = useState('')
  const t = (ro: string, en: string) => (locale === 'ro' ? ro : en)
  const shown = useMemo(() => {
    const q = fold(query.trim())
    if (q === '') return catalog
    return catalog.filter((s) => [s.label.ro, s.label.en, ...s.syn.ro, ...s.syn.en].some((w) => fold(w).includes(q)))
  }, [catalog, query])
  const groups = SET_GROUPS.map((g) => ({ ...g, sets: shown.filter((s) => setGroup(s) === g.key) })).filter((g) => g.sets.length > 0)
  return (
    <div className="od-rep-picker">
      <input
        type="search"
        className="form-control input-sm od-rep-picker-search"
        placeholder={t('Cauta: tichete, calculatoare, licente…', 'Search: tickets, computers, licenses…')}
        aria-label={t('Cauta datele', 'Search the data')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {groups.length === 0 ? <div className="od-rep-hint">{t('Nimic nu se potriveste.', 'Nothing matches.')}</div> : null}
      {groups.map((g) => (
        <div key={g.key} className="od-rep-picker-group">
          <div className="od-rep-picker-title">{g.label[locale]}</div>
          <div className="od-rep-picker-grid">
            {g.sets.map((s) => (
              <button key={s.key} type="button" className={`od-rep-set${s.key === current ? ' active' : ''}`} onClick={() => onPick(s.key)}>
                <span className="od-rep-set-name">{s.label[locale]}</span>
                <span className="od-rep-set-cols">
                  {s.columns
                    .slice(0, 4)
                    .map((c) => s.fields.find((f) => f.key === c)?.label[locale] ?? c)
                    .join(' · ')}
                </span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function Check({ label, checked, disabled, title, onChange }: { label: string; checked: boolean; disabled?: boolean; title?: string; onChange: (v: boolean) => void }) {
  return (
    <label className={`od-rep-check${disabled ? ' od-rep-check-off' : ''}`} title={title}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} /> {label}
    </label>
  )
}

export function Builder({ catalog, definition, values, locale, onChange }: Props) {
  const [picking, setPicking] = useState(false)
  const [dragged, setDragged] = useState<number | null>(null)
  const [presets, setPresets] = useState<FilterPreset[]>(() => (definition ? readPresets(definition.dataset) : []))
  const [presetsFor, setPresetsFor] = useState(definition?.dataset ?? '')
  const t = (ro: string, en: string) => (locale === 'ro' ? ro : en)
  const set = definition ? catalog.find((s) => s.key === definition.dataset) : undefined

  if (definition && definition.dataset !== presetsFor) {
    /* Alt set: filtrele lui salvate (la desen, nu intr-un efect - asa nu clipeste lista veche). */
    setPresetsFor(definition.dataset)
    setPresets(readPresets(definition.dataset))
  }

  const pick = (key: string) => {
    setPicking(false)
    if (key !== definition?.dataset) onChange({ dataset: key, view: 'table' })
  }

  if (!set || !definition) {
    return (
      <div className="od-rep-builder">
        <Step n={1} title={t('Ce date?', 'Choose the data')} hint={t('Alege una; rezultatul apare pe masura ce lucrezi.', 'Pick one; the result shows up as you go.')}>
          <DataPicker catalog={catalog} current={null} locale={locale} onPick={pick} />
        </Step>
        {[t('Ce masori', 'What to measure'), t('Grupat pe', 'Group by'), t('Filtre si perioada', 'Filters and period'), t('Cum arata', 'Show as')].map((title, i) => (
          <Step key={title} n={i + 2} title={title} disabled>
            {null}
          </Step>
        ))}
      </div>
    )
  }

  const aggregate = (definition.measures?.length ?? 0) > 0 || (definition.group_by?.length ?? 0) > 0
  const grouped = (definition.group_by?.length ?? 0) > 0
  const columns = definition.columns ?? set.columns
  const field = (key: string | undefined) => set.fields.find((f) => f.key === key)
  const patch = (p: Partial<Definition>, refit = false) => {
    const next: Definition = { ...definition, ...p }
    for (const [k, v] of Object.entries(p)) if (v === undefined) delete (next as unknown as Record<string, unknown>)[k]
    if (refit) next.view = fittingView(next)
    onChange(pruneDisplay(next, set))
  }
  const display: Display = definition.display ?? {}
  const setDisplay = (p: Partial<Display>) => {
    const next: Display = { ...display, ...p }
    for (const [k, v] of Object.entries(p)) if (v === undefined || v === false) delete (next as Record<string, unknown>)[k]
    patch({ display: Object.keys(next).length > 0 ? next : undefined })
  }

  const measures: Measure[] = definition.measures && definition.measures.length > 0 ? definition.measures : aggregate ? [{ agg: 'count' }] : []
  const measure: Measure | null = measures[0] ?? null
  const shape: Shape = measure ? measure.agg : 'list'
  const hasAgg = (agg: Measure['agg']) => agg === 'count' || set.fields.some((f) => f.aggs.includes(agg))
  const fieldsFor = (agg: Measure['agg']) => (agg === 'count' ? [] : set.fields.filter((f) => f.aggs.includes(agg)))
  const groupable = set.fields.filter((f) => f.group)
  const isMore = (MORE_AGGS as string[]).includes(shape)
  const planned = plannedColumns(definition, set)
  const numericCols = planned.filter((c) => c.numeric && c.role !== 'group')
  const additive = measure !== null && ADDITIVE.includes(measure.agg)
  const hasPeriod = !!definition.period && definition.period.key !== 'all'
  const view: View = definition.view ?? 'table'

  const choose = (next: Shape) => {
    if (next === 'list') {
      patch({ measures: undefined, group_by: undefined, sort: undefined, limit: undefined }, true)
      return
    }
    const preferred = set.default_measure?.agg === next ? set.default_measure.field : undefined
    const first = preferred ?? set.fields.find((f) => f.aggs.includes(next))?.key
    const head: Measure = next === 'count' || !first ? { agg: 'count' } : { agg: next, field: first }
    patch({ measures: [head, ...measures.slice(1)], columns: undefined }, true)
  }

  const setMeasure = (i: number, m: Measure | null) => {
    const list = [...measures]
    if (m === null) list.splice(i, 1)
    else list[i] = m
    /* Ordinea pe o masura care nu mai exista ar fi refuzata de server. */
    const sort = definition.sort && /^m\d+$/.test(definition.sort.by) && Number(definition.sort.by.slice(1)) >= list.length ? undefined : definition.sort
    patch({ measures: list, sort })
  }
  const measureOf = (agg: Measure['agg'], keep?: string): Measure => {
    if (agg === 'count') return { agg }
    const fields = fieldsFor(agg)
    return { agg, field: fields.find((f) => f.key === keep)?.key ?? fields[0]?.key }
  }

  const onDrop = (e: DragEvent, to: number) => {
    e.preventDefault()
    if (dragged === null || dragged === to) return
    const next = [...columns]
    const [moved] = next.splice(dragged, 1)
    next.splice(to, 0, moved!)
    setDragged(null)
    patch({ columns: next, display: remapColumns(definition, columns, next) })
  }
  const removeColumn = (key: string) => {
    const next = columns.filter((c) => c !== key)
    patch({ columns: next, display: remapColumns(definition, columns, next) })
  }

  const setGroupBy = (gi: number, key: string) => {
    const rest = [...(definition.group_by ?? [])]
    if (key === '') rest.splice(gi)
    else rest[gi] = field(key)?.type === 'date' ? { field: key, bucket: 'month' } : { field: key }
    const isGrouped = rest.length > 0
    /* Gruparea cere o socoteala: din lista se trece singur la "numara". */
    const ms = definition.measures && definition.measures.length > 0 ? definition.measures : isGrouped ? [{ agg: 'count' as const }] : undefined
    patch({ group_by: isGrouped ? rest : undefined, measures: ms, columns: isGrouped ? undefined : definition.columns, ...(isGrouped ? {} : { sort: undefined, limit: undefined }) }, true)
  }

  /* Ordinea: pe o coloana a rezultatului (grupat) sau pe orice camp (lista). */
  const sortOptions: { value: string; label: string }[] = aggregate
    ? planned.map((c) => ({ value: c.key, label: c.label[locale] }))
    : set.fields.filter((f) => f.sensitivity !== 'personal').map((f) => ({ value: f.key, label: f.label[locale] }))

  const filters = definition.filters ?? []
  const savePreset = () => {
    const name = window.prompt(t('Numele filtrelor salvate:', 'Name for the saved filters:'), '')?.trim()
    if (!name) return
    const list = [{ name, filters, ...(definition.match === 'any' ? { match: 'any' as const } : {}) }, ...presets.filter((p) => p.name !== name)]
    writePresets(definition.dataset, list)
    setPresets(list)
  }
  const applyPreset = (name: string) => {
    const p = presets.find((x) => x.name === name)
    if (!p) return
    const known = p.filters.filter((f) => set.fields.some((x) => x.key === f.field))
    patch({ filters: known.length > 0 ? known : undefined, match: p.match === 'any' ? 'any' : undefined })
  }
  const dropPreset = (name: string) => {
    const list = presets.filter((p) => p.name !== name)
    writePresets(definition.dataset, list)
    setPresets(list)
  }

  const need = NEEDS[view]
  const misfit =
    (need === 'nogroup' && grouped) || (need === 'group' && !grouped) || (need === 'two' && (definition.group_by?.length ?? 0) < 2)
      ? need === 'nogroup'
        ? t('Vederea asta arata o cifra: scoate gruparea de la pasul 3.', 'This view shows a number: remove the grouping in step 3.')
        : need === 'two'
          ? t('Barele stivuite cer doua grupari (pasul 3, "si apoi pe"); pana atunci se vad bare simple.', 'Stacked bars need two groupings (step 3, "then by"); until then simple bars are shown.')
          : t('Vederea asta cere o grupare (pasul 3).', 'This view needs a grouping (step 3).')
      : null

  return (
    <div className="od-rep-builder">
      <Step n={1} title={t('Ce date?', 'Choose the data')}>
        {picking ? (
          <DataPicker catalog={catalog} current={set.key} locale={locale} onPick={pick} />
        ) : (
          <div className="od-rep-picked">
            <span className="od-rep-picked-group">{SET_GROUPS.find((g) => g.key === setGroup(set))?.label[locale]}</span>
            <b>{set.label[locale]}</b>
            <button type="button" className="btn btn-link btn-sm" onClick={() => setPicking(true)}>
              {t('Schimba', 'Change')}
            </button>
          </div>
        )}
      </Step>

      <Step n={2} title={t('Ce masori', 'What to measure')}>
        <div className="od-rep-seg" role="radiogroup" aria-label={t('Ce masori', 'What to measure')}>
          {SHAPES.filter((s) => s.shape === 'list' || hasAgg(s.shape as Measure['agg'])).map((s) => (
            <button key={s.shape} type="button" role="radio" aria-checked={shape === s.shape} className={`od-rep-seg-btn${shape === s.shape ? ' active' : ''}`} onClick={() => choose(s.shape)}>
              <span className="od-rep-seg-mark" aria-hidden="true">
                {s.mark}
              </span>
              {s.label[locale]}
            </button>
          ))}
          {MORE_AGGS.some(hasAgg) ? (
            <select
              className={`form-control input-sm od-rep-seg-more${isMore ? ' active' : ''}`}
              aria-label={t('Alta socoteala', 'Other measure')}
              value={isMore ? shape : ''}
              onChange={(e) => e.target.value && choose(e.target.value as Shape)}
            >
              <option value="">{t('Altceva…', 'More…')}</option>
              {MORE_AGGS.filter(hasAgg).map((a) => (
                <option key={a} value={a}>
                  {MORE_LABELS[a]![locale]}
                </option>
              ))}
            </select>
          ) : null}
        </div>
        {measure && measure.agg !== 'count' ? (
          <label className="od-rep-field od-rep-field-row">
            <span>{AGG_LABELS[measure.agg][locale]}</span>
            <select className="form-control input-sm" value={measure.field ?? ''} onChange={(e) => setMeasure(0, { agg: measure.agg, field: e.target.value })}>
              {fieldsFor(measure.agg).map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label[locale]}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {measure?.agg === 'count' && measures.length === 1 ? <div className="od-rep-hint">{t(`Cate inregistrari sunt in ${set.label.ro.toLowerCase()}.`, `How many ${set.label.en.toLowerCase()} there are.`)}</div> : null}
        {aggregate ? (
          <div className="od-rep-measures">
            {measures.slice(1).map((m, k) => (
              <span key={k} className="od-rep-measure">
                <select className="form-control input-sm" aria-label={t('Socoteala', 'Measure')} value={m.agg} onChange={(e) => setMeasure(k + 1, measureOf(e.target.value as Measure['agg'], m.field))}>
                  {ALL_AGGS.filter(hasAgg).map((a) => (
                    <option key={a} value={a}>
                      {aggWord(a)[locale]}
                    </option>
                  ))}
                </select>
                {m.agg !== 'count' ? (
                  <select className="form-control input-sm" aria-label={t('Campul', 'Field')} value={m.field ?? ''} onChange={(e) => setMeasure(k + 1, { agg: m.agg, field: e.target.value })}>
                    {fieldsFor(m.agg).map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.label[locale]}
                      </option>
                    ))}
                  </select>
                ) : null}
                <button type="button" className="od-rep-x" aria-label={t('Scoate masura', 'Remove measure')} onClick={() => setMeasure(k + 1, null)}>
                  ×
                </button>
              </span>
            ))}
            {measures.length < MAX_MEASURES ? (
              <select className="form-control input-sm od-rep-add" value="" aria-label={t('Adauga o masura', 'Add a measure')} onChange={(e) => e.target.value && setMeasure(measures.length, measureOf(e.target.value as Measure['agg']))}>
                <option value="">{t('+ inca o masura', '+ another measure')}</option>
                {ALL_AGGS.filter(hasAgg).map((a) => (
                  <option key={a} value={a}>
                    {aggWord(a)[locale]}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
        ) : null}
        {!aggregate ? (
          <div className="od-rep-columns">
            <span className="od-rep-caption">{t('Coloane (trage ca sa le muti)', 'Columns (drag to reorder)')}</span>
            {columns.map((key, i) => (
              <span key={key} className="od-rep-col" draggable onDragStart={() => setDragged(i)} onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, i)}>
                {field(key)?.label[locale] ?? key}
                <button type="button" className="od-rep-x" aria-label={t('Scoate', 'Remove')} onClick={() => removeColumn(key)} disabled={columns.length === 1}>
                  ×
                </button>
              </span>
            ))}
            <select className="form-control input-sm od-rep-add" value="" aria-label={t('Adauga o coloana', 'Add a column')} onChange={(e) => e.target.value && patch({ columns: [...columns, e.target.value] })}>
              <option value="">{t('+ coloana', '+ column')}</option>
              {set.fields
                .filter((f) => !columns.includes(f.key))
                .map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label[locale]}
                  </option>
                ))}
            </select>
          </div>
        ) : null}
      </Step>

      <Step n={3} title={t('Grupat pe', 'Group by')} hint={t('optional', 'optional')}>
        <div className="od-rep-inline-row">
          {[0, 1].map((gi) => {
            const g = definition.group_by?.[gi]
            const gf = g ? field(g.field) : undefined
            if (gi === 1 && !definition.group_by?.[0]) return null
            return (
              <label key={gi} className="od-rep-field">
                <span>{gi === 0 ? t('Pe', 'By') : t('si apoi pe', 'then by')}</span>
                <span className="od-rep-inline">
                  <select className="form-control input-sm" value={g?.field ?? ''} onChange={(e) => setGroupBy(gi, e.target.value)}>
                    <option value="">{gi === 0 ? t('Fara grupare', 'No grouping') : t('(nimic)', '(nothing)')}</option>
                    {groupable.map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.label[locale]}
                      </option>
                    ))}
                  </select>
                  {gf?.type === 'date' ? (
                    <select
                      className="form-control input-sm"
                      aria-label={t('Pe ce interval', 'Per')}
                      value={g?.bucket ?? 'month'}
                      onChange={(e) => {
                        const rest = [...(definition.group_by ?? [])]
                        rest[gi] = { field: g!.field, bucket: e.target.value as Bucket }
                        patch({ group_by: rest })
                      }}
                    >
                      {BUCKETS.map((b) => (
                        <option key={b} value={b}>
                          {t('pe ', 'per ')}
                          {BUCKET_LABELS[b][locale]}
                        </option>
                      ))}
                    </select>
                  ) : null}
                </span>
              </label>
            )
          })}
        </div>
        {!grouped ? <div className="od-rep-hint">{t('De exemplu pe sediu, pe stare sau pe luna.', 'For example by office, by status or by month.')}</div> : null}
        <div className="od-rep-inline-row">
          <label className="od-rep-field">
            <span>{t('Ordine', 'Sort by')}</span>
            <span className="od-rep-inline">
              <select
                className="form-control input-sm"
                aria-label={t('Ordine', 'Sort by')}
                value={definition.sort?.by ?? ''}
                onChange={(e) => patch({ sort: e.target.value === '' ? undefined : { by: e.target.value, dir: definition.sort?.dir ?? 'desc' } })}
              >
                <option value="">{t('implicita', 'default')}</option>
                {sortOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {definition.sort ? (
                <select className="form-control input-sm" aria-label={t('Sensul', 'Direction')} value={definition.sort.dir} onChange={(e) => patch({ sort: { by: definition.sort!.by, dir: e.target.value as 'asc' | 'desc' } })}>
                  <option value="desc">{t('descrescator', 'descending')}</option>
                  <option value="asc">{t('crescator', 'ascending')}</option>
                </select>
              ) : null}
            </span>
          </label>
          <label className="od-rep-field">
            <span>{grouped ? t('Cel mult', 'At most') : t('Cel mult (randuri)', 'At most (rows)')}</span>
            <input
              type="number"
              min={1}
              max={grouped ? 50 : 100000}
              className="form-control input-sm od-rep-num-input"
              aria-label={t('Cel mult', 'At most')}
              value={definition.limit ?? ''}
              placeholder={grouped ? '50' : t('toate', 'all')}
              onChange={(e) => patch({ limit: e.target.value === '' ? undefined : Math.max(1, Math.trunc(Number(e.target.value)) || 1) })}
            />
          </label>
        </div>
        {grouped ? (
          <div className="od-rep-inline-row od-rep-checks">
            <Check
              label={t('Restul ca "altele"', 'The rest as "others"')}
              checked={!!display.others}
              disabled={!additive || definition.limit === undefined}
              title={!additive ? t('Numai la numarari si sume.', 'Only for counts and sums.') : definition.limit === undefined ? t('Pune intai "cel mult".', 'Set "at most" first.') : undefined}
              onChange={(v) => setDisplay({ others: v })}
            />
            <Check label={t('Rand de total', 'Total row')} checked={!!display.totals} onChange={(v) => setDisplay({ totals: v })} />
            <Check label={t('% din total', '% of total')} checked={!!display.share} disabled={!additive} title={!additive ? t('Numai la numarari si sume.', 'Only for counts and sums.') : undefined} onChange={(v) => setDisplay({ share: v })} />
          </div>
        ) : null}
      </Step>

      <Step n={4} title={t('Filtre si perioada', 'Filters and period')}>
        <div className="od-rep-inline-row">
          <label className="od-rep-field">
            <span>{t('Perioada', 'Period')}</span>
            <select
              className="form-control input-sm"
              value={definition.period?.key ?? 'all'}
              disabled={set.time === null && !definition.time_field}
              onChange={(e) => {
                const key = e.target.value as PeriodKey
                if (key === 'all') patch({ period: undefined })
                else if (key === 'last_n_days') patch({ period: { key, n: 14 } })
                else if (key === 'custom') {
                  const today = new Date().toISOString().slice(0, 10)
                  patch({ period: { key, from: today, to: today } })
                } else patch({ period: { key } })
              }}
            >
              {PERIODS.map((p) => (
                <option key={p} value={p}>
                  {PERIOD_LABELS[p][locale]}
                </option>
              ))}
            </select>
          </label>
          {definition.period?.key === 'last_n_days' ? (
            <label className="od-rep-field">
              <span>{t('Zile', 'Days')}</span>
              <input
                type="number"
                min={1}
                max={366}
                className="form-control input-sm od-rep-num-input"
                value={definition.period.n ?? 14}
                onChange={(e) => patch({ period: { key: 'last_n_days', n: Math.max(1, Math.min(366, Number(e.target.value) || 1)) } })}
              />
            </label>
          ) : null}
          {definition.period?.key === 'custom' ? (
            <>
              <label className="od-rep-field">
                <span>{t('De la', 'From')}</span>
                <input type="date" className="form-control input-sm" value={definition.period.from ?? ''} onChange={(e) => patch({ period: { ...definition.period!, from: e.target.value } })} />
              </label>
              <label className="od-rep-field">
                <span>{t('Pana la', 'To')}</span>
                <input type="date" className="form-control input-sm" value={definition.period.to ?? ''} onChange={(e) => patch({ period: { ...definition.period!, to: e.target.value } })} />
              </label>
            </>
          ) : null}
          {hasPeriod && grouped ? (
            <Check label={t('Compara cu perioada dinainte', 'Compare with the previous period')} checked={!!display.compare} onChange={(v) => setDisplay({ compare: v })} />
          ) : null}
        </div>
        {set.time === null && !definition.time_field ? <div className="od-rep-hint">{t('Datele astea nu au o data, deci nici perioada.', 'This data has no date, so no period.')}</div> : null}
        {hasPeriod && !grouped && aggregate ? <div className="od-rep-hint">{t('Cifra se compara singura cu perioada dinainte.', 'The number is compared with the previous period on its own.')}</div> : null}
        {filters.length > 1 ? (
          <div className="od-rep-seg od-rep-match" role="radiogroup" aria-label={t('Cum se leaga filtrele', 'How filters combine')}>
            {(['all', 'any'] as const).map((m) => (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={(definition.match ?? 'all') === m}
                className={`od-rep-seg-btn${(definition.match ?? 'all') === m ? ' active' : ''}`}
                onClick={() => patch({ match: m === 'any' ? 'any' : undefined })}
              >
                {m === 'all' ? t('Toate conditiile', 'All conditions') : t('Oricare conditie', 'Any condition')}
              </button>
            ))}
          </div>
        ) : null}
        <div className="od-rep-filters">
          {filters.map((f, i) => (
            <FilterRow
              key={i}
              set={set}
              filter={f}
              values={values}
              locale={locale}
              onChange={(next) => {
                const list = [...filters]
                if (next === null) list.splice(i, 1)
                else list[i] = next
                patch({ filters: list.length > 0 ? list : undefined, ...(list.length < 2 ? { match: undefined } : {}) })
              }}
            />
          ))}
          <select
            className="form-control input-sm od-rep-add"
            value=""
            aria-label={t('Adauga un filtru', 'Add a filter')}
            onChange={(e) => {
              const f = field(e.target.value)
              if (!f) return
              const op = OPS[f.type][0]!
              const value = f.type === 'bool' ? 1 : f.type === 'enum' ? [] : ''
              patch({ filters: [...filters, { field: f.key, op, value } as Filter] })
            }}
          >
            <option value="">{t('+ filtru', '+ filter')}</option>
            {set.fields
              .filter((f) => f.filter && f.sensitivity !== 'personal')
              .map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label[locale]}
                </option>
              ))}
          </select>
        </div>
        <div className="od-rep-presets">
          {presets.length > 0 ? (
            <select className="form-control input-sm" value="" aria-label={t('Filtre salvate', 'Saved filters')} onChange={(e) => e.target.value && applyPreset(e.target.value)}>
              <option value="">{t('Filtre salvate…', 'Saved filters…')}</option>
              {presets.map((p) => (
                <option key={p.name} value={p.name}>
                  {p.name}
                </option>
              ))}
            </select>
          ) : null}
          {filters.length > 0 ? (
            <button type="button" className="btn btn-link btn-sm" onClick={savePreset}>
              {t('Salveaza filtrele', 'Save these filters')}
            </button>
          ) : null}
          {presets.length > 0 ? (
            <select className="form-control input-sm od-rep-preset-drop" value="" aria-label={t('Sterge filtre salvate', 'Delete saved filters')} onChange={(e) => e.target.value && dropPreset(e.target.value)}>
              <option value="">{t('Sterge…', 'Delete…')}</option>
              {presets.map((p) => (
                <option key={p.name} value={p.name}>
                  {p.name}
                </option>
              ))}
            </select>
          ) : null}
        </div>
      </Step>

      <Step n={5} title={t('Cum arata', 'Show as')}>
        <div className="od-rep-seg" role="radiogroup" aria-label={t('Vedere', 'View')}>
          {ALL_VIEWS.map((v) => (
            <button key={v} type="button" role="radio" aria-checked={view === v} className={`od-rep-seg-btn${view === v ? ' active' : ''}`} onClick={() => patch({ view: v })}>
              <span className="od-rep-seg-mark" aria-hidden="true">
                {MARKS[v]}
              </span>
              {VIEW_LABELS[v][locale]}
            </button>
          ))}
        </div>
        {misfit ? <div className="od-rep-hint">{misfit}</div> : null}
        {aggregate && !grouped ? (
          <div className="od-rep-inline-row">
            <label className="od-rep-field">
              <span>{t('Tinta', 'Target')}</span>
              <input
                type="number"
                className="form-control input-sm od-rep-num-input"
                aria-label={t('Tinta', 'Target')}
                value={display.target ?? ''}
                onChange={(e) => setDisplay({ target: e.target.value === '' || !Number.isFinite(Number(e.target.value)) ? undefined : Number(e.target.value) })}
              />
            </label>
            {display.target !== undefined ? (
              <label className="od-rep-field">
                <span>{t('E mai bine', 'Better is')}</span>
                <select className="form-control input-sm" value={display.better ?? 'up'} onChange={(e) => setDisplay({ better: e.target.value === 'down' ? 'down' : undefined })}>
                  <option value="up">{t('mai mult', 'more')}</option>
                  <option value="down">{t('mai putin', 'less')}</option>
                </select>
              </label>
            ) : null}
          </div>
        ) : null}
        {numericCols.length > 0 ? (
          <div className="od-rep-rules">
            <span className="od-rep-caption">{t('Culori dupa valoare', 'Colour by value')}</span>
            {(display.rules ?? []).map((r, i) => (
              <span key={i} className="od-rep-rule">
                <select className="form-control input-sm" aria-label={t('Coloana', 'Column')} value={r.col} onChange={(e) => setRule(i, { ...r, col: e.target.value })}>
                  {numericCols.map((c) => (
                    <option key={c.key} value={c.key}>
                      {display.labels?.[c.key] ?? c.label[locale]}
                    </option>
                  ))}
                </select>
                <select className="form-control input-sm od-rep-rule-op" aria-label={t('Conditia', 'Condition')} value={r.op} onChange={(e) => setRule(i, { ...r, op: e.target.value as Rule['op'] })}>
                  {RULE_OPS.map((o) => (
                    <option key={o.op} value={o.op}>
                      {o.text}
                    </option>
                  ))}
                </select>
                <input type="number" className="form-control input-sm od-rep-num-input" aria-label={t('Valoarea', 'Value')} value={r.value} onChange={(e) => setRule(i, { ...r, value: Number(e.target.value) || 0 })} />
                <select className={`form-control input-sm od-rep-rule-${r.color}`} aria-label={t('Culoarea', 'Colour')} value={r.color} onChange={(e) => setRule(i, { ...r, color: e.target.value as RuleColor })}>
                  {RULE_COLORS.map((c) => (
                    <option key={c.color} value={c.color}>
                      {c.label[locale]}
                    </option>
                  ))}
                </select>
                <button type="button" className="od-rep-x" aria-label={t('Scoate regula', 'Remove rule')} onClick={() => setRule(i, null)}>
                  ×
                </button>
              </span>
            ))}
            {(display.rules?.length ?? 0) < 10 ? (
              <button type="button" className="btn btn-link btn-sm" onClick={() => setRule(display.rules?.length ?? 0, { col: numericCols[0]!.key, op: 'gt', value: 0, color: 'red' })}>
                {t('+ regula de culoare', '+ colour rule')}
              </button>
            ) : null}
          </div>
        ) : null}
        <details className="od-rep-labels">
          <summary>{t('Numele coloanelor', 'Column names')}</summary>
          <div className="od-rep-inline-row">
            {planned.map((c) => (
              <label key={c.key} className="od-rep-field">
                <span>{c.label[locale]}</span>
                <input
                  type="text"
                  maxLength={60}
                  className="form-control input-sm"
                  placeholder={c.label[locale]}
                  value={display.labels?.[c.key] ?? ''}
                  onChange={(e) => {
                    const labels = { ...(display.labels ?? {}) }
                    if (e.target.value.trim() === '') delete labels[c.key]
                    else labels[c.key] = e.target.value
                    setDisplay({ labels: Object.keys(labels).length > 0 ? labels : undefined })
                  }}
                />
              </label>
            ))}
          </div>
        </details>
      </Step>
    </div>
  )

  function setRule(i: number, r: Rule | null) {
    const list = [...(display.rules ?? [])]
    if (r === null) list.splice(i, 1)
    else list[i] = r
    setDisplay({ rules: list.length > 0 ? list : undefined })
  }
}

/** O lista scrisa cu virgula ("Bucuresti, Cluj"): textul ramane cum il scrie omul, valoarea e lista. */
function ListInput({ value, onChange }: { value: (string | number)[]; onChange: (v: string[]) => void }) {
  const [text, setText] = useState(value.join(', '))
  return (
    <input
      type="text"
      className="form-control input-sm"
      placeholder="a, b, c"
      value={text}
      onChange={(e) => {
        setText(e.target.value)
        onChange(
          e.target.value
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s !== '')
        )
      }}
    />
  )
}

function FilterRow({
  set,
  filter,
  values,
  locale,
  onChange,
}: {
  set: CatalogSet
  filter: Filter
  values: Record<string, ValueItem[]>
  locale: Locale
  onChange: (next: Filter | null) => void
}) {
  const f = set.fields.find((x) => x.key === filter.field)
  if (!f) return null
  /* Valorile reale ale campului, sau - la coduri (stare, risc) - etichetele din catalog. */
  const known = [...(values[f.key] ?? []), ...(f.options ?? []).filter((o) => typeof o.value === 'number').map((o) => ({ id: o.value as number, label: o.label[locale], n: 0 }))].filter((v) => v.id !== null)
  const needsValue = filter.op !== 'empty' && filter.op !== 'nempty'
  const type = f.type === 'date' ? 'date' : ['number', 'duration', 'bytes'].includes(f.type) ? 'number' : 'text'
  const input = () => {
    if (!needsValue) return null
    if (f.type === 'bool') {
      return (
        <select className="form-control input-sm" value={String(filter.value ?? 1)} onChange={(e) => onChange({ ...filter, value: Number(e.target.value) })}>
          <option value="1">{locale === 'ro' ? 'Da' : 'Yes'}</option>
          <option value="0">{locale === 'ro' ? 'Nu' : 'No'}</option>
        </select>
      )
    }
    if (f.type === 'enum' && (filter.op === 'in' || filter.op === 'nin') && known.length > 0) {
      const chosen = Array.isArray(filter.value) ? filter.value.map(Number) : []
      return (
        <select
          multiple
          className="form-control input-sm od-rep-multi"
          value={chosen.map(String)}
          onChange={(e) => onChange({ ...filter, value: [...e.target.selectedOptions].map((o) => Number(o.value)) })}
        >
          {known.map((v) => (
            <option key={v.id!} value={String(v.id)}>
              {v.label}
            </option>
          ))}
        </select>
      )
    }
    if (f.type === 'text' && (filter.op === 'in' || filter.op === 'nin')) {
      return <ListInput value={Array.isArray(filter.value) ? filter.value : []} onChange={(v) => onChange({ ...filter, value: v })} />
    }
    if (filter.op === 'between') {
      const pair = Array.isArray(filter.value) ? filter.value : ['', '']
      const at = (i: 0 | 1, raw: string) => {
        const next = [pair[0] ?? '', pair[1] ?? '']
        next[i] = type === 'number' ? (raw === '' ? '' : Number(raw)) : raw
        onChange({ ...filter, value: next })
      }
      return (
        <span className="od-rep-inline">
          <input type={type} className="form-control input-sm" aria-label={locale === 'ro' ? 'De la' : 'From'} value={String(pair[0] ?? '')} onChange={(e) => at(0, e.target.value)} />
          <span>{locale === 'ro' ? 'si' : 'and'}</span>
          <input type={type} className="form-control input-sm" aria-label={locale === 'ro' ? 'Pana la' : 'To'} value={String(pair[1] ?? '')} onChange={(e) => at(1, e.target.value)} />
        </span>
      )
    }
    return (
      <input
        type={type}
        className="form-control input-sm"
        value={Array.isArray(filter.value) ? filter.value.join(', ') : String(filter.value ?? '')}
        onChange={(e) => onChange({ ...filter, value: type === 'number' ? Number(e.target.value) : e.target.value })}
      />
    )
  }
  const changeOp = (op: FilterOp) => {
    const list = op === 'in' || op === 'nin'
    const value = op === 'between' ? ['', ''] : list ? [] : Array.isArray(filter.value) ? '' : filter.value
    onChange({ ...filter, op, value })
  }
  return (
    <span className="od-rep-filter">
      <b>{f.label[locale]}</b>
      <select className="form-control input-sm" value={filter.op} onChange={(e) => changeOp(e.target.value as FilterOp)}>
        {OPS[f.type].map((op) => (
          <option key={op} value={op}>
            {(f.type === 'text' ? TEXT_LIST_LABELS[op] : undefined)?.[locale] ?? OP_LABELS[op][locale]}
          </option>
        ))}
      </select>
      {input()}
      <button type="button" className="od-rep-x" aria-label={locale === 'ro' ? 'Scoate filtrul' : 'Remove filter'} onClick={() => onChange(null)}>
        ×
      </button>
    </span>
  )
}
